import { motion } from 'framer-motion';

const TreeVisualization = ({ step, lessonId }) => {
  if (!step || !step.treeCtx) return null;

  const { treeCtx, visited = [], current, queue = [], callStack = [], pId, qId, isLCA, found, justVisited } = step;
  const { positions, nodes, edges, width } = treeCtx;
  const visitedSet = new Set(visited);

  const height = Math.max(...nodes.map(n => positions[n.id]?.y || 0)) + 80;

  const getNodeStyle = (nodeId) => {
    if (isLCA === nodeId) return { fill: '#22C55E', stroke: '#4ADE80', shadow: 'drop-shadow(0 0 15px rgba(34,197,94,0.7))' };
    if (found === nodeId) return { fill: '#FFB800', stroke: '#FFC520', shadow: 'drop-shadow(0 0 15px rgba(255,184,0,0.6))' };
    if (current === nodeId) return { fill: '#FFB800', stroke: '#FFC520', shadow: 'drop-shadow(0 0 12px rgba(255,184,0,0.6))' };
    if (nodeId === pId) return { fill: '#8B5CF6', stroke: '#A78BFA', shadow: 'drop-shadow(0 0 8px rgba(139,92,246,0.4))' };
    if (nodeId === qId) return { fill: '#EC4899', stroke: '#F472B6', shadow: 'drop-shadow(0 0 8px rgba(236,72,153,0.4))' };
    if (visitedSet.has(nodeId)) return { fill: '#007AFF', stroke: '#00D4FF', shadow: 'drop-shadow(0 0 8px rgba(0,122,255,0.4))' };
    return { fill: '#1A1A20', stroke: '#71717A', shadow: 'none' };
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#0F0F12] border border-white/10 rounded-lg p-3 overflow-auto">
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="mx-auto">
          {/* Edges */}
          {edges.map((edge, i) => {
            const from = positions[edge.from];
            const to = positions[edge.to];
            if (!from || !to) return null;
            const bothVisited = visitedSet.has(edge.from) && visitedSet.has(edge.to);
            return (
              <line
                key={i}
                x1={from.x} y1={from.y}
                x2={to.x} y2={to.y}
                stroke={bothVisited ? '#007AFF' : '#4A4A50'}
                strokeWidth={bothVisited ? 2 : 1.5}
              />
            );
          })}

          {/* Nodes */}
          {nodes.map(node => {
            const pos = positions[node.id];
            if (!pos) return null;
            const style = getNodeStyle(node.id);
            return (
              <g key={node.id}>
                <motion.circle
                  cx={pos.x} cy={pos.y} r={22}
                  fill={style.fill}
                  stroke={style.stroke}
                  strokeWidth={current === node.id ? 3 : 2}
                  filter={style.shadow}
                  animate={{ r: current === node.id ? 24 : 22 }}
                  transition={{ duration: 0.3 }}
                  data-testid={`tree-node-${node.id}`}
                />
                <text
                  x={pos.x} y={pos.y + 5}
                  textAnchor="middle"
                  fontSize="14"
                  fontFamily="JetBrains Mono, monospace"
                  fontWeight="bold"
                  fill={visitedSet.has(node.id) || current === node.id || isLCA === node.id ? 'white' : '#A1A1AA'}
                >
                  {node.val}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Traversal */}
      {step.traversal && (
        <div className="bg-[#0F0F12] border border-[#007AFF]/20 rounded p-3">
          <div className="text-[#007AFF] text-xs font-code mb-1">TRAVERSAL ORDER</div>
          <div className="font-code text-white text-sm">
            {step.traversal.length === 0 ? '(none)' : step.traversal.join(' → ')}
          </div>
        </div>
      )}

      {/* Queue for BFS */}
      {queue.length > 0 && (
        <div className="bg-[#0F0F12] border border-[#FFB800]/20 rounded p-3">
          <div className="text-[#FFB800] text-xs font-code mb-2">QUEUE (FIFO)</div>
          <div className="flex gap-1 flex-wrap">
            {queue.map((id, i) => {
              const n = nodes.find(nd => nd.id === id);
              return (
                <div key={i} className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded px-2 py-1 text-xs font-code text-[#FFB800]">
                  {n?.val}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Call Stack for Inorder */}
      {callStack.length > 0 && lessonId === 'inorder-traversal' && (
        <div className="bg-[#0F0F12] border border-purple-500/20 rounded p-3">
          <div className="text-purple-400 text-xs font-code mb-2">CALL STACK ({callStack.length})</div>
          <div className="flex gap-1 flex-wrap">
            {callStack.map((id, i) => {
              const n = nodes.find(nd => nd.id === id);
              return (
                <div key={i} className="bg-purple-500/10 border border-purple-500/30 rounded px-2 py-1 text-xs font-code text-purple-400">
                  inorder({n?.val})
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* LCA legend */}
      {lessonId === 'lca' && step.p !== undefined && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#0F0F12] border border-purple-500/20 rounded p-3 text-center">
            <div className="text-purple-400 text-xs font-code mb-1">p</div>
            <div className="text-white text-lg font-code font-bold">{step.p}</div>
          </div>
          <div className="bg-[#0F0F12] border border-pink-500/20 rounded p-3 text-center">
            <div className="text-pink-400 text-xs font-code mb-1">q</div>
            <div className="text-white text-lg font-code font-bold">{step.q}</div>
          </div>
        </div>
      )}

      {/* Result */}
      {step.result !== undefined && (
        <div className="bg-gradient-to-r from-green-500/10 to-green-400/10 border border-green-500/30 rounded-lg p-4 text-center">
          <div className="text-[#A1A1AA] text-sm font-body mb-1">
            {lessonId === 'lca' ? 'Lowest Common Ancestor' : 'Traversal Result'}
          </div>
          <div className="text-green-400 text-2xl font-code font-bold">{step.result}</div>
        </div>
      )}
    </div>
  );
};

export default TreeVisualization;
